<template>
  <div v-if="visibleBanners.length" class="banner-notifications">
    <div
      v-for="banner in visibleBanners"
      :key="banner.bannerNotificationId"
      class="alert alert-dismissible banner-notification"
      :style="variantStyle(banner.bannerType)"
      role="alert"
    >
      <button
        type="button"
        class="close"
        aria-label="Close"
        :style="{ color: bannerTextColor(banner.bannerType), opacity: 0.85, textShadow: 'none' }"
        @click="dismiss(banner)"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <!-- messageHtml is server-rendered Markdown (HTML-escaped at source), so v-html is safe here. -->
      <div class="banner-notification-body" v-html="banner.messageHtml"></div>
    </div>
  </div>
</template>

<script>
// ARB-9910 — customer-facing banner strip. Replaces the old hardcoded maintenance banner (ARB-10636).
// Dismissal is client-side per browser (localStorage), matching the shared contract used by the Portal cookie.
import bannersService from 'src/banners.service';
import { bearerTokenKey } from 'src/constants/localStorage';

const STORAGE_KEY = 'bannerDismissals';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Mirror of PLDataCore BannerNotification.BannerTypes.
const BANNER_TYPE = {
  INFO: 10,
  MAINTENANCE: 20,
  CRITICAL: 30,
};

// Keep in sync with PLDataCore BannerNotification.BannerColors.
const BANNER_COLOR = {
  [BANNER_TYPE.INFO]: '#248A3D', // green
  [BANNER_TYPE.MAINTENANCE]: '#008299', // teal
  [BANNER_TYPE.CRITICAL]: '#FF9500', // orange
};

export default {
  name: 'BannerNotifications',

  data() {
    return {
      banners: [],
      dismissals: this.readDismissals(),
    };
  },

  computed: {
    visibleBanners() {
      return this.banners.filter(b => this.isVisible(b));
    },
  },

  async mounted() {
    // Only call the API when logged in; fail silent so the login flow is never blocked.
    const token = window.localStorage.getItem(bearerTokenKey);
    if (!token) {
      return;
    }

    try {
      const data = await bannersService.getActiveBanners();
      this.banners = Array.isArray(data) ? data : [];
    } catch (e) {
      // Network/401 — never surface to the user.
    }
  },

  methods: {
    bannerBackground(bannerType) {
      return BANNER_COLOR[bannerType] || BANNER_COLOR[BANNER_TYPE.INFO];
    },

    // Readable foreground: black on the light orange, white on the darker teal/green.
    bannerTextColor(bannerType) {
      return bannerType === BANNER_TYPE.CRITICAL ? '#000000' : '#FFFFFF';
    },

    variantStyle(bannerType) {
      const background = this.bannerBackground(bannerType);
      return {
        backgroundColor: background,
        borderColor: background,
        color: this.bannerTextColor(bannerType),
      };
    },

    readDismissals() {
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {
        return {};
      }
    },

    writeDismissals(map) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    },

    // Show iff: no entry, OR the version changed (banner edited), OR the redisplay interval has elapsed.
    isVisible(banner) {
      const entry = this.dismissals[String(banner.bannerNotificationId)];
      if (!entry) {
        return true;
      }
      if (entry.ver !== banner.version) {
        return true;
      }
      if (banner.redisplayAfterDays != null && entry.at) {
        const elapsed = Date.now() - new Date(entry.at).getTime();
        if (elapsed >= banner.redisplayAfterDays * ONE_DAY_MS) {
          return true;
        }
      }
      return false;
    },

    dismiss(banner) {
      const map = { ...this.dismissals };
      map[String(banner.bannerNotificationId)] = {
        at: new Date().toISOString(),
        ver: banner.version,
      };

      // Prune entries for banners no longer active, keeping localStorage small.
      const activeIds = this.banners.map(b => String(b.bannerNotificationId));
      Object.keys(map).forEach(key => {
        if (activeIds.indexOf(key) === -1) {
          delete map[key];
        }
      });

      this.dismissals = map;
      this.writeDismissals(map);
    },
  },
};
</script>

<style scoped>
.banner-notifications {
  position: sticky;
  top: 0;
  z-index: 1030;
}

.banner-notification.alert {
  margin-bottom: 0;
  border-radius: 0;
}

/* Keep links legible against the colored strips. */
.banner-notification .banner-notification-body a {
  text-decoration: underline;
  font-weight: bold;
  color: inherit;
}

.banner-notification .banner-notification-body :last-child {
  margin-bottom: 0;
}
</style>
